import React, { useEffect, useState } from 'react';
import { loadFaq, loadLanguages, saveFaq } from '../utils/Network';
import { sortLanguages } from '../utils';
import NamesWidget from '../components/widgets/NamesWidget';

export default function FaqContent({ id }) {
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState(false);
    const [names, setNames] = useState([]);
    const [languages, setLanguages] = useState([]);
    /** Kept for API only (no UI); true for new FAQ, loaded from API when editing */
    const [status, setStatus] = useState(true);

    useEffect(() => {
        loadLanguages().then((langs) => {
            const sorted = sortLanguages(langs || []);
            setLanguages(sorted);
        });

        if (id) {
            loadFaq(id).then((faq) => {
                setStatus(faq.status == 1);
                if (faq.names?.length) {
                    setNames(faq.names);
                } else if (faq.language) {
                    setNames([
                        {
                            language: faq.language,
                            question: faq.question || '',
                            answer: faq.answer || '',
                        },
                    ]);
                } else {
                    setNames([]);
                }
            });
        }
    }, [id]);

    useEffect(() => {
        if (!done) return undefined;
        const timer = window.setTimeout(() => setDone(false), 2000);
        return () => window.clearTimeout(timer);
    }, [done]);

    const stubQuestionEntry = (language) => ({
        language,
        question: '',
        answer: '',
    });

    const pickPrimaryLanguageId = () => {
        const sorted = languages || [];
        const def = sorted.find((l) => l.is_default)?.id ?? sorted[0]?.id;
        return def != null ? parseInt(def, 10) : null;
    };

    const SaveChanges = () => {
        setError(null);
        setDone(false);

        const usableNames = (names || []).filter((item) => item?.question || item?.answer);
        const preferredLang = pickPrimaryLanguageId();

        let selectedEntry = null;
        if (preferredLang != null) {
            selectedEntry = usableNames.find(
                (item) =>
                    parseInt(item.language, 10) === preferredLang &&
                    item?.question &&
                    item?.answer
            );
        }
        if (!selectedEntry) {
            selectedEntry = usableNames.find((item) => item?.question && item?.answer) || usableNames[0];
        }

        const languageToSave = selectedEntry?.language ?? preferredLang;

        if (!selectedEntry?.question) {
            setError('Please enter Question');
            return;
        }

        if (!selectedEntry?.answer) {
            setError('Please enter Answer');
            return;
        }

        if (languageToSave == null) {
            setError('Please complete Question and Answer in at least one language.');
            return;
        }

        setLoading(true);
        saveFaq(
            id,
            selectedEntry.question,
            selectedEntry.answer,
            status,
            languageToSave,
            usableNames
        ).then((faq) => {
            if (faq.id) {
                setDone('Changes saved successfully');
                setNames(faq.names || usableNames);
                if (faq.status !== undefined && faq.status !== null) {
                    setStatus(faq.status == 1);
                }
            }
            setLoading(false);
        });
    };

    const pageTitle = id ? 'Edit FAQ' : 'Add New FAQ';

    return (
        <div className="content-wrapper cms-faq-edit-page">
            <section className="content-header">
                <h3 className="box-title mb-10">
                    <span className="sr-only">FAQ</span>
                </h3>
                <ol className="breadcrumb">
                    <li>
                        <a href="/cms">
                            <i className="fa fa-dashboard" /> Home
                        </a>
                    </li>
                    <li>
                        <a href="/cms/faqs">Faqs</a>
                    </li>
                    <li className="active">{id ? 'Edit' : 'Add'}</li>
                </ol>
            </section>

            <section className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-md-12">
                            <div className="box cms-dark-box no-border">
                                <div className="box-header with-border">
                                    <h3 className="box-title">{pageTitle}</h3>
                                </div>
                                <div className="box-body padding-10 bg-ddd">
                                    <NamesWidget
                                        languages={languages}
                                        names={names}
                                        onNamesChange={setNames}
                                        onActiveLanguageChange={() => setDone(false)}
                                        titleField="question"
                                        descriptionField="answer"
                                        stubTitleEntry={stubQuestionEntry}
                                        stubDescriptionEntry={stubQuestionEntry}
                                        allowNewEntryFromDescription={!id}
                                    />
                                </div>
                                <div className="box-footer clearfix">
                                    {error && (
                                        <div className="cms-faq-edit-messages mb-10">
                                            <div className="alert alert-danger mb-0 clearfix">
                                                {error}
                                            </div>
                                        </div>
                                    )}
                                    <div className="cms-faq-edit-actions clearfix">
                                        <a href="/cms/faqs" className="btn btn-default pull-left">
                                            Cancel
                                        </a>
                                        <button
                                            type="button"
                                            className="btn btn-primary pull-right"
                                            onClick={SaveChanges}
                                            disabled={loading}
                                        >
                                            Save Changes
                                        </button>
                                    </div>
                                    {(loading || done) && (
                                        <div className="cms-faq-edit-toasts mt-10">
                                            {loading && (
                                                <div
                                                    className="cms-faq-save-loading alert alert-info mb-0 clearfix"
                                                    role="status"
                                                    aria-live="polite"
                                                >
                                                    Please Wait...
                                                </div>
                                            )}
                                            {done && (
                                                <div
                                                    className="cms-faq-save-success alert alert-success mb-0 clearfix"
                                                    role="status"
                                                >
                                                    {done}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
